import { BRAND_CLASSES } from '@/constants/theme'

export type LabelSize = 'xs' | 'sm' | 'md'

/**
 * Get consistent label classes based on size.
 * - 'xs': Panel-style labels (text-xs uppercase tracking-wide, muted color, mb-1)
 * - 'sm' / 'md': Modal-style labels (text-sm font-medium, standard color, mb-2)
 */
export function getLabelClasses(labelSize: LabelSize = 'sm', labelClassName = ''): string {
  const size = labelSize === 'xs' ? 'text-xs uppercase tracking-wide' : 'text-sm'
  const weight = labelSize === 'xs' ? '' : 'font-medium'
  const color = labelSize === 'xs' ? 'text-slate-500 dark:text-slate-400' : BRAND_CLASSES.INPUT.LABEL
  const margin = labelSize === 'xs' ? 'mb-1' : 'mb-2'
  return `block ${size} ${weight} ${color} ${margin} ${labelClassName}`.trim()
}

export type ControlDensity = 'comfortable' | 'compact'

/**
 * Input sizing by density. `comfortable` is the app default (unchanged); `compact` tightens
 * padding, radius, and text for dense, panel-style forms. Opt-in per field.
 */
export function getInputSizing(density: ControlDensity = 'comfortable'): string {
  return density === 'compact'
    ? 'px-3 py-1.5 text-sm rounded-md'
    : 'px-3 py-2 rounded-lg'
}

/**
 * Field-label classes that respect density. `compact` uses a small, sentence-case, medium-weight
 * muted label; `comfortable` falls back to the existing labelSize scale.
 */
export function getFieldLabelClasses(
  density: ControlDensity = 'comfortable',
  labelSize: LabelSize = 'sm',
  labelClassName = '',
): string {
  if (density === 'compact') {
    return `block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5 ${labelClassName}`.trim()
  }
  return getLabelClasses(labelSize, labelClassName)
}
